import * as geom from "js/core/utilities/geom";
import { ContentBlockType, NodeType, ShapeType } from "common/constants";
import { _ } from "js/vendor";

import { CollectionElement } from "../../base/CollectionElement";
import { TextElement } from "../../base/Text/TextElement";
import { SVGPolygonElement, SVGPolylineElement } from "../../base/SVGElement";
import { GridComponent } from "../../components/Grid";
import { getContentItemFromType } from "../../base/ContentItem";

class XYGraph extends CollectionElement {
    get maxItemCount() {
        return 100;
    }

    get allowDragDropElements() {
        return false;
    }

    getChildItemType(model) {
        if (model.nodeType == NodeType.CIRCLE) {
            return getContentItemFromType(NodeType.FLEX_CIRCLE);
        } else {
            return getContentItemFromType(model.nodeType || NodeType.FLEX_CIRCLE);
        }
    }

    getChildOptions(model) {
        return {
            syncFontSizeWithSiblings: true
        };
    }

    getItemSelectionUIType(item) {
        return "XYGraphItemSelection";
    }

    getAllowedNodeTypes() {
        return [NodeType.CIRCLE, NodeType.BOX, NodeType.TEXT, NodeType.BULLET_TEXT, NodeType.NUMBERED_TEXT, NodeType.CONTENT_AND_TEXT, NodeType.CONTENT];
        // return [NodeType.CIRCLE, NodeType.BOX, NodeType.CONTENT_AND_TEXT, NodeType.CAPSULE, NodeType.TEXT, NodeType.BULLET_TEXT, NodeType.NUMBERED_TEXT, NodeType.CONTENT];
    }

    get canEditNumberedTextContentItemMarkerValue() {
        return true;
    }

    get defaultBlockType() {
        return ContentBlockType.TITLE;
    }

    get defaultItemData() {
        return {
            x: 0.1 + Math.random() * .65,
            y: 0.1 + Math.random() * .75,
            size: this.itemCollection[this.itemCollection.length - 1].size || 100,
            // color: this.itemCollection[this.itemCollection.length - 1].color || "accent1",
            // color: "accent" + parseInt(Math.random() * app.currentTheme.getAccentColorsCount() + 1),
            textDirection: "auto",
            nodeType: this.itemCollection[this.itemCollection.length - 1].nodeType || NodeType.CIRCLE
        };
    }

    get arrowHeadSize() {
        return new geom.Size(this.styles.xAxis.strokeWidth * 3.5, this.styles.xAxis.strokeWidth * 3.5 * 1.25);
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    async convertToAuthoring(exportElementAsGroup, exportChildrenAsGroup) {
        // add the grid
        let grid;
        if (this.model.showGrid) {
            grid = [this.grid];

            if (this.model.showAxis) {
                grid.push(
                    this.xAxis,
                    this.yAxis
                );
            }

            if (this.model.showArrowHeads) {
                grid.push(
                    this.xAxisArrowHeadLeft,
                    this.xAxisArrowHeadRight,
                    this.yAxisArrowHeadBottom,
                    this.yAxisArrowHeadTop,
                );
            }

            grid = _.compact(grid);

            if (grid.length) {
                await exportChildrenAsGroup(grid);
            }
        }

        // check for labels
        ["x", "y"]
            .forEach(async type => {
                const prop = `${type}AxisLabel`;
                const label = this[prop];
                if (label?.model?.[prop]?.text) {
                    await exportElementAsGroup(label);
                }
            });

        let quadrants;
        if (this.model.showQuadrants) {
            quadrants = [this.hRule, this.vRule];
            if (this.model.showQuadrantLabels) {
                quadrants.push(
                    this.label1,
                    this.label2,
                    this.label3,
                    this.label4
                );
            }

            quadrants = _.compact(quadrants);
            if (quadrants.length) {
                await exportChildrenAsGroup(quadrants);
            }
        }

        // export each of the child items
        for (const item of this.itemElements) {
            await exportElementAsGroup(item);
        }

        // check for grid annotations
        if (this.canvas.annotations) {
            await exportChildrenAsGroup(this.canvas.annotations);
        }
    }

    _build() {
        if (this.model.showGrid) {
            this.grid = this.addElement("grid", () => GridComponent);
        }

        if (this.model.showAxis) {
            this.xAxis = this.addElement("xAxis", () => SVGPolylineElement);
            this.xAxis.layer = -1;
            this.yAxis = this.addElement("yAxis", () => SVGPolylineElement);
            this.yAxis.layer = -1;
            if (this.model.showAxisLabels) {
                this.yAxisLabel = this.addElement("yAxisLabel", () => XYGraphAxisLabel, {
                    autoWidth: true,
                    autoHeight: true,
                    placeholder: "Type label",
                    selectAllOnEdit: true,
                    showFocusedBlock: false
                });
                this.xAxisLabel = this.addElement("xAxisLabel", () => XYGraphAxisLabel, {
                    autoWidth: true,
                    autoHeight: true,
                    placeholder: "Type label",
                    selectAllOnEdit: true,
                    showFocusedBlock: false
                });

                if (this.model.axisPosition == "center") {
                    this.yAxisLabelEnd = this.addElement("yAxisLabelEnd", () => XYGraphAxisLabel, {
                        autoWidth: true,
                        autoHeight: true,
                        placeholder: "Type label",
                        selectAllOnEdit: true,
                        showFocusedBlock: false
                    });

                    this.xAxisLabelEnd = this.addElement("xAxisLabelEnd", () => XYGraphAxisLabel, {
                        autoWidth: true,
                        autoHeight: true,
                        placeholder: "Type label",
                        selectAllOnEdit: true,
                        showFocusedBlock: false
                    });
                }
            }

            if (this.model.showArrowHeads) {
                if (this.model.showAxis) {
                    this.xAxisArrowHeadRight = this.addElement("xAxisArrowHeadRight", () => SVGPolygonElement);
                    this.yAxisArrowHeadTop = this.addElement("yAxisArrowHeadTop", () => SVGPolygonElement);
                    if (this.model.axisPosition == "center") {
                        this.xAxisArrowHeadLeft = this.addElement("xAxisArrowHeadLeft", () => SVGPolygonElement);
                        this.yAxisArrowHeadBottom = this.addElement("yAxisArrowHeadBottom", () => SVGPolygonElement);
                    }
                }
            }
        }

        if (this.model.showQuadrants) {
            this.hRule = this.addElement("hRule", () => SVGPolylineElement);
            this.hRule.layer = -1;
            this.vRule = this.addElement("vRule", () => SVGPolylineElement);
            this.vRule.layer = -1;
            if (this.model.showQuadrantLabels) {
                this.label1 = this.addElement("label1", () => TextElement, {
                    autoWidth: true,
                    autoHeight: true,
                    selectAllOnEdit: true,
                });
                this.label2 = this.addElement("label2", () => TextElement, {
                    autoWidth: true,
                    autoHeight: true,
                    selectAllOnEdit: true
                });
                this.label3 = this.addElement("label3", () => TextElement, {
                    autoWidth: true,
                    autoHeight: true,
                    selectAllOnEdit: true
                });
                this.label4 = this.addElement("label4", () => TextElement, {
                    autoWidth: true,
                    autoHeight: true,
                    selectAllOnEdit: true
                });
            }
        }

        this.buildItems();

        // this.nodesCollection = this.addElement("nodesCollection", () => XYGraphNodesCollection);

        // this is the group we will append any textMasks to - this makes sure the masks are over the graph but below everything else
        //TODO
        // this.textMaskGroup = this.elements.graph.svg.group();
    }

    _calcProps(props, options) {
        const { size, children } = props;

        const AXIS_PADDING = 20;

        const bounds = new geom.Rect(0, 0, size.width, size.height);
        let plotBoxBounds = bounds.clone();

        if (this.model.showAxis && this.model.showAxisLabels) {
            if (this.model.axisPosition == "center") {
                plotBoxBounds = plotBoxBounds.deflate({
                    left: AXIS_PADDING,
                    right: AXIS_PADDING,
                    top: AXIS_PADDING,
                    bottom: AXIS_PADDING
                });
            } else {
                plotBoxBounds = plotBoxBounds.deflate({
                    left: AXIS_PADDING,
                    bottom: AXIS_PADDING
                });
            }
        }

        if (this.model.showGrid) {
            // Migration from old model values
            const gridSize = 300 / this.model.gridSize;
            const majorTickSpan = 4;
            // Adjusting bounds
            const majorTickSize = gridSize * 4;
            const width = Math.floor(plotBoxBounds.width / majorTickSize) * majorTickSize;
            const height = Math.floor(plotBoxBounds.height / majorTickSize) * majorTickSize;
            plotBoxBounds = plotBoxBounds.deflate({ right: plotBoxBounds.width - width, top: plotBoxBounds.height - height });

            const gridProps = this.grid.calcProps(plotBoxBounds.size, {
                showMinorGridLines: false,
                majorTickSpan,
                gridSize
            });
            gridProps.bounds = plotBoxBounds;
            gridProps.layer = -1;
        }

        if (this.model.showAxis) {
            const arrowHeadSize = this.arrowHeadSize;
            const arrowLength = this.model.showArrowHeads ? arrowHeadSize.height : 0;

            const xAxisProps = this.xAxis.createProps({ layer: -1 });
            const yAxisProps = this.yAxis.createProps({ layer: -1 });

            if (this.model.axisPosition == "center") {
                xAxisProps.path = [[plotBoxBounds.centerH, plotBoxBounds.top + arrowLength], [plotBoxBounds.centerH, plotBoxBounds.bottom - arrowLength]];
                yAxisProps.path = [[plotBoxBounds.left + arrowLength, plotBoxBounds.centerV], [plotBoxBounds.right - arrowLength, plotBoxBounds.centerV]];

                if (this.model.showArrowHeads) {
                    const yAxisArrowHeadTop = this.yAxisArrowHeadTop.createProps({
                        path: [[plotBoxBounds.centerH, plotBoxBounds.top + arrowLength], [plotBoxBounds.centerH - arrowHeadSize.width / 2, plotBoxBounds.top + arrowLength], [plotBoxBounds.centerH, plotBoxBounds.top + arrowLength - arrowHeadSize.height], [plotBoxBounds.centerH + arrowHeadSize.width / 2, plotBoxBounds.top + arrowLength]],
                    });
                    this.yAxisArrowHeadTop.styles.applyStyles(this.styles.yArrow);

                    const xAxisArrowHeadRight = this.xAxisArrowHeadRight.createProps({
                        path: [[plotBoxBounds.right - arrowLength, plotBoxBounds.centerV], [plotBoxBounds.right - arrowLength, plotBoxBounds.centerV - arrowHeadSize.width / 2], [plotBoxBounds.right - arrowLength + arrowHeadSize.height, plotBoxBounds.centerV], [plotBoxBounds.right - arrowLength, plotBoxBounds.centerV + arrowHeadSize.width / 2]],
                    });
                    this.xAxisArrowHeadRight.styles.applyStyles(this.styles.xArrow);

                    const xAxisArrowHeadLeft = this.xAxisArrowHeadLeft.createProps({
                        path: [[plotBoxBounds.left, plotBoxBounds.centerV], [plotBoxBounds.left + arrowHeadSize.height, plotBoxBounds.centerV - arrowHeadSize.width / 2], [plotBoxBounds.left + arrowHeadSize.height, plotBoxBounds.centerV + arrowHeadSize.width / 2]],
                    });
                    this.xAxisArrowHeadLeft.styles.applyStyles(this.styles.xArrow);

                    const yAxisArrowHeadBottom = this.yAxisArrowHeadBottom.createProps({
                        path: [[plotBoxBounds.centerH - arrowHeadSize.width / 2, plotBoxBounds.bottom - arrowLength], [plotBoxBounds.centerH + arrowHeadSize.width / 2, plotBoxBounds.bottom - arrowLength], [plotBoxBounds.centerH, plotBoxBounds.bottom - arrowLength + arrowHeadSize.height]],
                    });
                    this.yAxisArrowHeadBottom.styles.applyStyles(this.styles.yArrow);
                }
            } else {
                xAxisProps.path = [[plotBoxBounds.left, plotBoxBounds.top + arrowLength], [plotBoxBounds.left, plotBoxBounds.bottom]];
                yAxisProps.path = [[plotBoxBounds.left, plotBoxBounds.bottom], [plotBoxBounds.right - arrowLength, plotBoxBounds.bottom]];

                if (this.model.showArrowHeads) {
                    const yAxisArrowHeadTop = this.yAxisArrowHeadTop.createProps({
                        path: [[plotBoxBounds.left, plotBoxBounds.top + arrowLength], [plotBoxBounds.left - arrowHeadSize.width / 2, plotBoxBounds.top + arrowLength], [plotBoxBounds.left, plotBoxBounds.top + arrowLength - arrowHeadSize.height], [plotBoxBounds.left + arrowHeadSize.width / 2, plotBoxBounds.top + arrowLength]],
                    });
                    this.yAxisArrowHeadTop.styles.applyStyles(this.styles.yArrow);

                    const xAxisArrowHeadRight = this.xAxisArrowHeadRight.createProps({
                        path: [[plotBoxBounds.right - arrowLength, plotBoxBounds.bottom], [plotBoxBounds.right - arrowLength, plotBoxBounds.bottom - arrowHeadSize.width / 2], [plotBoxBounds.right - arrowLength + arrowHeadSize.height, plotBoxBounds.bottom], [plotBoxBounds.right - arrowLength, plotBoxBounds.bottom + arrowHeadSize.width / 2]],
                    });
                    this.xAxisArrowHeadRight.styles.applyStyles(this.styles.xArrow);
                }
            }

            if (this.model.showAxisLabels) {
                const yAxisLabelProps = this.yAxisLabel.calcProps(size);
                yAxisLabelProps.bounds = new geom.Rect(-yAxisLabelProps.size.width / 2, plotBoxBounds.centerV - yAxisLabelProps.size.height / 2, yAxisLabelProps.size);
                yAxisLabelProps.rotate = -90;

                const xAxisLabelProps = this.xAxisLabel.calcProps(size);
                xAxisLabelProps.bounds = new geom.Rect(plotBoxBounds.centerH - this.xAxisLabel.textBounds.size.width / 2, plotBoxBounds.bottom, xAxisLabelProps.size);

                if (this.model.axisPosition == "center") {
                    const yAxisLabelEndProps = this.yAxisLabelEnd.calcProps(size);
                    yAxisLabelEndProps.bounds = new geom.Rect(size.width - yAxisLabelEndProps.size.width / 2, plotBoxBounds.centerV - yAxisLabelEndProps.size.height / 2, yAxisLabelEndProps.size);
                    yAxisLabelEndProps.rotate = 90;

                    const xAxisLabelEndProps = this.xAxisLabelEnd.calcProps(size);
                    xAxisLabelEndProps.bounds = new geom.Rect(plotBoxBounds.centerH - this.xAxisLabelEnd.textBounds.size.width / 2, plotBoxBounds.top - 40, xAxisLabelEndProps.size);
                }
            }
        }

        if (this.model.showQuadrants) {
            const vRule = this.vRule.createProps({});
            vRule.path = [[plotBoxBounds.centerH, plotBoxBounds.top], [plotBoxBounds.centerH, plotBoxBounds.bottom]];
            vRule.layer = -1;
            this.vRule.styles.applyStyles(this.styles.quadrantLine);

            const hRule = this.hRule.createProps({});
            hRule.path = [[plotBoxBounds.left, plotBoxBounds.centerV], [plotBoxBounds.right, plotBoxBounds.centerV]];
            hRule.layer = -1;
            this.hRule.styles.applyStyles(this.styles.quadrantLine);

            if (this.model.showQuadrantLabels) {
                const labelPadding = 10;

                const label1 = this.label1.calcProps(size);
                label1.bounds = new geom.Rect(plotBoxBounds.left + labelPadding, plotBoxBounds.top + labelPadding, plotBoxBounds.width / 2 - labelPadding * 2, label1.size.height);
                label1.layer = -1;

                const label2 = this.label2.calcProps(size);
                label2.bounds = new geom.Rect(plotBoxBounds.width / 2 + plotBoxBounds.left + labelPadding, plotBoxBounds.top + labelPadding, plotBoxBounds.width / 2 - labelPadding * 2, label2.size.height);
                label2.layer = -1;

                const label3 = this.label3.calcProps(size);
                label3.bounds = new geom.Rect(plotBoxBounds.left + labelPadding, plotBoxBounds.bottom - labelPadding - label3.size.height, plotBoxBounds.width / 2 - labelPadding * 2, label3.size.height);
                label3.layer = -1;

                const label4 = this.label4.calcProps(size);
                label4.bounds = new geom.Rect(plotBoxBounds.width / 2 + plotBoxBounds.left + labelPadding, plotBoxBounds.bottom - labelPadding - label4.size.height, plotBoxBounds.width / 2 - labelPadding * 2, label4.size.height);
                label4.layer = -1;
            }
        }

        this.itemElements.forEach((node, nodeIdx) => {
            const nodeX = plotBoxBounds.left + node.model.x * plotBoxBounds.width;
            const nodeY = plotBoxBounds.top + node.model.y * plotBoxBounds.height;
            const nodeProps = node.calcProps(plotBoxBounds.size);
            nodeProps.bounds = new geom.Rect(nodeX, nodeY, nodeProps.size);

            // if (this.useLegacyNodes) {
            //     // Reverse-layering the nodes to mimic the pre-refactor behavior
            //     nodeProps.layer = this.itemElements.length - nodeIdx;
            // } else {
            nodeProps.layer = nodeIdx;
            // }
        });

        return { size };
    }

    async pasteFromClipboard({ dataType, model }) {
        if (dataType === "element") {
            const modelToPaste = _.cloneDeep(model);
            while (this.itemElements.filter(item => item.model.x === modelToPaste.x && item.model.y === modelToPaste.y).length > 0) {
                modelToPaste.x += 0.05;
                modelToPaste.y += 0.05;
            }
            delete modelToPaste.id;

            const { id } = this.addItem(modelToPaste);
            await this.canvas.updateCanvasModel(false);
            return this.getChild(id);
        }
    }
}

class XYGraphAxisLabel extends TextElement {
    get selectionBounds() {
        if (Math.abs(this.calculatedProps.rotate) === 90) {
            let selectionBounds = super.selectionBounds;
            return new geom.Rect(selectionBounds.centerH - selectionBounds.height / 2, selectionBounds.centerV - selectionBounds.width / 2, selectionBounds.height, selectionBounds.width);
        } else {
            return super.selectionBounds;
        }
    }

    get autoWidth() {
        return true;
    }

    get autoHeight() {
        return true;
    }

    get placeholderPrompt() {
        return "Type label";
    }

    get textAlign() {
        return "center";
    }

    get allowAlignment() {
        return false;
    }

    get maxLines() {
        return 1;
    }
}

export const elements = {
    XYGraph,
};
